import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppProfileService } from 'src/app/services/profile.service';
import { Location } from '@angular/common';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { EMPTY, Observable, switchMap, take, tap } from 'rxjs';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { KENDO_FILESELECT, SelectEvent } from '@progress/kendo-angular-upload';
import { CustomerService } from 'src/app/services/customer.service';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { Customer } from 'src/app/common/Customer';
import { HttpErrorResponse } from '@angular/common/http';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { TfaSetupComponent } from '../tfa-configuration/tfa-configuration.component';
import { UserNameEditComponent } from '../user-name-edit/user-name-edit.component';
import { CustomerContactEditComponent } from '../customer-contact-edit/customer-contact-edit.component';
import { ProfileRoleConstants } from '../../common/ProfileRole';
import { CustomSellerInfoEditComponent } from '../custom-seller-info-edit/custom-seller-info-edit.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PDFModule } from '@progress/kendo-angular-grid';
import { KENDO_PDFEXPORT } from '@progress/kendo-angular-pdf-export';
import { CallApiService } from '../../services/api-action.service';
import { Router } from '@angular/router';
import { AsyncClickDirective, AsyncClickLock } from '../../directives/async-click.directive';
import { readAsDataUrl } from 'src/app/common/browser-files';

/** The large-logo upload on the profile page; its type is inferred from here (#199). */
function buildLogoForm(fb: FormBuilder, largeImageFile: unknown) {
  return fb.group({
    largeFiles: [largeImageFile],
  });
}
type LogoForm = ReturnType<typeof buildLogoForm>;

@Component({
    imports: [
    TabStripModule,
    TfaSetupComponent,
    UserNameEditComponent,
    CustomerContactEditComponent,
    CustomSellerInfoEditComponent,
    ReactiveFormsModule,
    KENDO_FILESELECT,
    FontAwesomeModule,
    PDFModule,
    KENDO_PDFEXPORT,
    AsyncClickDirective
],
    selector: 'app-shop-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css']
})
export class ProfileComponent implements OnInit {
  fb = inject(FormBuilder);
  private appProfileService = inject(AppProfileService);
  private location = inject(Location);
  private customerService = inject(CustomerService);
  private callApiService = inject(CallApiService);
  private router = inject(Router);

  private destroyRef = inject(DestroyRef);

  public profile: ExtendedProfile = new ExtendedProfile();
  // Backend #670: the customer's contact block is maintainable by the customer's admin
  // users only (the BFF enforces it) — the "Kontaktdaten" tab shows for them alone.
  public isAdmin = false;
  public faFilePdf = faFilePdf;
  public largeImageFile: any;
  public largeFiles: Array<File> = [];
  public customerForm: LogoForm;
  public submitted = false;
  // Save (logo) and delete-profile share a lock: neither fires while the other runs.
  public profileLock: AsyncClickLock = { busy: false };

  ngOnInit(): void {
    this.appProfileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((profile) => {
      this.profile = profile;
      this.isAdmin = profile?.role == ProfileRoleConstants.RoleIdAdmin;
      this.customerForm = buildLogoForm(this.fb, this.largeImageFile);
    });

    if (this.profile?.customer) {
      this.customerService
        .getById(this.profile.customer.id)
        .pipe(
          switchMap((customer: Customer) => {
            this.customerForm = buildLogoForm(this.fb, this.largeImageFile);
            return customer.hasLargeImage ? this.customerService.getImageByProductId(customer.id, true) : EMPTY;
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((image) => readAsDataUrl(image, (url) => (this.largeImageFile = url)));
    }
  }

  onBackClicked() {
    this.location.back();
  }

  selectImageEventHandler(e: SelectEvent, largeImage: boolean) {
    // Only the large image has a preview on this page.
    if (largeImage) {
      readAsDataUrl(e.files[0].rawFile, (url) => (this.largeImageFile = url));
    }
  }

  submitForm(): Observable<unknown> {
    if (!this.customerForm.valid) {
      return EMPTY;
    }
    this.largeFiles = this.customerForm.controls.largeFiles.value;
    this.submitted = true;

    if (!this.profile?.customer.id) {
      return EMPTY;
    }
    return this.callApiService.callWithToasts$(
      this.SaveLargeImageIfChanged(),
      $localize`Firmenlogo wurde aktualisiert.`,
      $localize`Beim Aktualisieren des Firmenlogos ist ein Fehler aufgetreten.`
    );
  }

  private SaveLargeImageIfChanged(): Observable<Customer | any> {
    if (this.largeFiles && this.largeFiles[0]) {
      return this.customerService.updateImage(this.profile.customer.id, true, this.largeFiles[0]);
    } else return EMPTY;
  }

  deleteProfile(): Observable<unknown> {
    if (!confirm($localize`Möchten Sie Ihr Profil wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return EMPTY;
    }
    // tap keeps the existing success/error navigation; the error still propagates so
    // [asyncClick] resets busy (its catchError swallows it after this runs).
    return this.appProfileService.deleteProfile().pipe(
      take(1),
      tap({
        next: () => {
          // Store message in session storage so it persists through the logout redirect
          sessionStorage.setItem('profileDeletedMessage', $localize`Ihr Profil wurde erfolgreich gelöscht.`);
          // Navigate to logout, which will eventually redirect to login/home
          this.router.navigate(['/authentication/logout'], {
            queryParams: { returnUrl: '/message' },
            state: { local: true }
          });
        },
        error: (error: HttpErrorResponse) => {
          this.router.navigate(['/message'], {
            queryParams: {
              message: $localize`Beim Löschen des Profils ist ein Fehler aufgetreten.`,
              type: 'error',
              returnUrl: '/shop-profile',
              returnButtonText: $localize`Zurück zum Profil`
            }
          });
          console.error('Error deleting profile:', error);
        }
      })
    );
  }
}
